import { isValidPhoneNumber } from "libphonenumber-js";
import { useEffect, useMemo, type ReactNode } from "react";
import { z } from "zod";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useTranslations } from "@/shared/lang/useTranslations";

// The field prefills the country's dial code, which is not a number yet.
const isBlank = (value: string) => /^\+?\d{0,4}$/.test(value.replace(/\s/g, ""));

const isValidPhone = (value: string) => {
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
};

export interface NotificationPhoneFieldProps {
  label: ReactNode;
  description?: ReactNode;
  /** E.164. Read once: remount (`key`) to load another value. */
  defaultValue?: string;
  /** ISO 3166-1 alpha-2 code used to prefill the country. */
  defaultCountry?: string;
  disabled?: boolean;
  /** Receives the number in E.164 once it is valid, `""` while it is empty, and `null` while it is invalid. */
  onChange: (phone: string | null) => void;
}

/** A phone input that reports a usable number (or none) instead of raw keystrokes. */
export function NotificationPhoneField({
  label,
  description,
  defaultValue = "",
  defaultCountry,
  disabled,
  onChange,
}: NotificationPhoneFieldProps) {
  const { t } = useTranslations("notificationPreferences");

  const schema = useMemo(
    () =>
      z.object({
        phone: z.string().refine((v) => isBlank(v) || isValidPhone(v), { message: t("send_test_invalid") }),
      }),
    [t],
  );
  const form = useZodForm(schema, { phone: defaultValue });
  const phone = form.watch("phone");

  useEffect(() => {
    if (isBlank(phone)) onChange("");
    else onChange(isValidPhone(phone) ? phone : null);
  }, [phone, onChange]);

  return (
    <ColocaForm form={form} onSubmit={() => undefined} className="space-y-0">
      <ColocaPhoneField
        name="phone"
        label={label}
        description={description}
        defaultCountry={defaultCountry}
        placeholder={t("send_test_placeholder")}
        disabled={disabled}
      />
    </ColocaForm>
  );
}
